import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  RotateCcw,
  BookOpen,
  Laptop,
  CheckCircle2,
  FileText,
  AlertCircle,
} from "lucide-react";

import { workshopsService } from "@/api/workshopsService";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/SiteLayout";

import WorkshopDetailHero from "@/components/workshops/WorkshopDetailHero";
import WorkshopLearningOutcomes from "@/components/workshops/WorkshopLearningOutcomes";
import WorkshopAgenda from "@/components/workshops/WorkshopAgenda";
import WorkshopInstructorCard from "@/components/workshops/WorkshopInstructorCard";
import WorkshopGallery from "@/components/workshops/WorkshopGallery";
import WorkshopStickyCTA from "@/components/workshops/WorkshopStickyCTA";
import WorkshopCard from "@/components/workshops/WorkshopCard";

/**
 * Helper: Safely renders narrative text with basic markdown-style blocks
 * (subheadings and bullet points) without unsafe raw HTML injection.
 */
function SafeOverviewRenderer({ content }) {
  if (!content) return null;

  // Split into paragraphs / blocks
  const blocks = content.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);

  return (
    <div className="space-y-4">
      {blocks.map((block, idx) => {
        // Subheading: ### or ##
        if (block.startsWith("### ") || block.startsWith("## ")) {
          const text = block.replace(/^#{2,3}\s+/, "");
          return (
            <h3
              key={idx}
              className="text-lg sm:text-xl font-bold tracking-tight text-white pt-2 first:pt-0"
            >
              {text}
            </h3>
          );
        }

        // Bullet list
        if (block.includes("\n- ") || block.startsWith("- ")) {
          const items = block
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.startsWith("- "))
            .map((line) => line.replace(/^- \s*/, ""));

          return (
            <ul key={idx} className="space-y-2.5 my-3">
              {items.map((item, itemIdx) => {
                // Parse bold prefix like **Production-First Codebase:**
                const boldMatch = item.match(/^\*\*(.*?)\*\*(.*)$/);
                if (boldMatch) {
                  return (
                    <li
                      key={itemIdx}
                      className="flex items-start gap-2.5 text-sm sm:text-base text-white/80 leading-relaxed"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2" />
                      <span>
                        <strong className="text-white font-semibold">
                          {boldMatch[1]}
                        </strong>
                        {boldMatch[2]}
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={itemIdx}
                    className="flex items-start gap-2.5 text-sm sm:text-base text-white/80 leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2" />
                    <span>{item}</span>
                  </li>
                );
              })}
            </ul>
          );
        }

        // Standard narrative paragraph
        return (
          <p
            key={idx}
            className="text-sm sm:text-base text-white/75 leading-relaxed font-normal"
          >
            {block}
          </p>
        );
      })}
    </div>
  );
}

/**
 * ============================================================
 * PUBLIC WORKSHOP DETAIL PAGE (/workshops/:slug)
 * ============================================================
 * Premium, conversion-focused workshop detail experience:
 * - Two-column responsive layout (65% content / 35% sticky CTA)
 * - Safe data loading strictly through workshopsService
 * - Draft workshops hidden from public view (404 state)
 * - Dynamic SEO meta tags and EducationEvent JSON-LD schema
 * - Related workshops recommendation section
 */
export default function WorkshopDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [workshop, setWorkshop] = useState(null);
  const [relatedWorkshops, setRelatedWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch workshop data by slug
  const loadWorkshop = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    // Call service with allowDraft = false (strictly public visibility)
    workshopsService
      .getWorkshopBySlug(slug, false)
      .then((data) => {
        if (process.env.NODE_ENV !== "production" && data) {
          console.groupCollapsed(`[Workshop Public Pipeline Diagnostic] Slug: "${data.slug}"`);
          console.log("Workshop ID:", data.id);
          console.log("show_highlights (DB raw):", data.show_highlights);
          console.log("lifecycle_stage:", data.lifecycle_stage);
          console.log("is_completed:", data.is_completed);
          console.log("Total media records (gallery + highlights):", (data.gallery?.length || 0) + (data.published_highlights_count || 0));
          console.log("Published highlight count:", data.published_highlights_count);
          console.log("is_highlights_eligible:", data.is_highlights_eligible);
          console.log("Highlight items passed to UI:", (data.highlights || []).map((h) => ({ id: h.id, media_type: h.media_type, media_format: h.media_format, is_published: h.is_published })));
          console.groupEnd();
        }
        setWorkshop(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load workshop details:", err);
        setError("Unable to load this workshop.");
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    loadWorkshop();
  }, [loadWorkshop]);

  // Load related workshops when workshop data arrives
  useEffect(() => {
    if (!workshop) {
      setRelatedWorkshops([]);
      return;
    }

    let mounted = true;
    workshopsService.getWorkshops().then((allPublished) => {
      if (!mounted) return;

      // Exclude current workshop
      const others = allPublished.filter(
        (w) => w.id !== workshop.id && w.slug !== workshop.slug
      );

      // Prioritize same category, then upcoming
      others.sort((a, b) => {
        const aSameCategory = a.category === workshop.category;
        const bSameCategory = b.category === workshop.category;
        if (aSameCategory && !bSameCategory) return -1;
        if (!aSameCategory && bSameCategory) return 1;
        return new Date(a.date || 0) - new Date(b.date || 0);
      });

      // Max 3 cards
      setRelatedWorkshops(others.slice(0, 3));
    });

    return () => {
      mounted = false;
    };
  }, [workshop]);

  // Dynamic SEO & EducationEvent JSON-LD Schema
  const defaultTitleRef = useRef(document.title);
  const defaultDescRef = useRef(
    document.querySelector('meta[name="description"]')?.getAttribute("content") || ""
  );

  useEffect(() => {
    if (!workshop) return;

    const fallbackTitle = defaultTitleRef.current;
    const fallbackDesc = defaultDescRef.current;

    // 1. Dynamic Page Title
    document.title = `${workshop.title} | Shiveda IT Services`;

    // 2. Dynamic Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && workshop.short_description) {
      metaDesc.setAttribute("content", workshop.short_description);
    }

    // 3. Inject EducationEvent JSON-LD script
    const scriptId = `jsonld-workshop-${workshop.id || slug}`;
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }

    const jsonLdPayload = {
      "@context": "https://schema.org",
      "@type": "EducationEvent",
      name: workshop.title,
      description: workshop.short_description,
      startDate: workshop.date,
      endDate: workshop.date,
      eventAttendanceMode:
        workshop.mode === "Online"
          ? "https://schema.org/OnlineEventAttendanceMode"
          : workshop.mode === "Offline"
          ? "https://schema.org/OfflineEventAttendanceMode"
          : "https://schema.org/MixedEventAttendanceMode",
      eventStatus:
        workshop.status === "Cancelled"
          ? "https://schema.org/EventCancelled"
          : "https://schema.org/EventScheduled",
      location:
        workshop.mode === "Online"
          ? {
              "@type": "VirtualLocation",
              url: window.location.href,
            }
          : {
              "@type": "Place",
              name: workshop.venue || "Shiveda Innovation Hub",
              address: workshop.venue || "Noida, India",
            },
      performer: {
        "@type": "Person",
        name: workshop.instructor?.name || "Shiveda Engineering Mentor",
        jobTitle: workshop.instructor?.role || "Instructor",
      },
      offers: {
        "@type": "Offer",
        price: workshop.is_free ? "0" : String(workshop.price || 0),
        priceCurrency: "INR",
        availability: workshop.is_registration_open
          ? "https://schema.org/InStock"
          : "https://schema.org/SoldOut",
        url: window.location.href,
      },
      organizer: {
        "@type": "Organization",
        name: "Shiveda IT Services",
        url: "https://shivedaitservices.com",
      },
    };

    script.textContent = JSON.stringify(jsonLdPayload);

    // Cleanup on unmount or slug change
    return () => {
      document.title = fallbackTitle;
      if (metaDesc && fallbackDesc) {
        metaDesc.setAttribute("content", fallbackDesc);
      }
      const activeScript = document.getElementById(scriptId);
      if (activeScript) {
        activeScript.remove();
      }
    };
  }, [workshop, slug]);

  // ============================================================
  // A. LOADING SKELETON STATE
  // ============================================================
  if (loading) {
    return (
      <div className="w-full px-5 py-10 xl:px-10 2xl:px-14 max-w-7xl mx-auto space-y-8 animate-pulse">
        {/* Breadcrumb skeleton */}
        <div className="h-4 w-48 rounded bg-white/10" />

        {/* Hero skeleton */}
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="h-6 w-28 rounded-full bg-white/10" />
            <div className="h-6 w-20 rounded-full bg-white/10" />
          </div>
          <div className="h-10 sm:h-14 w-3/4 rounded-xl bg-white/10" />
          <div className="h-5 w-full max-w-2xl rounded bg-white/10" />
        </div>

        {/* Key info strip skeleton */}
        <div className="h-24 w-full rounded-2xl bg-white/5 border border-white/10" />

        {/* Two-column layout skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4">
          <div className="lg:col-span-8 space-y-6">
            <div className="h-48 rounded-3xl bg-white/5 border border-white/10" />
            <div className="h-64 rounded-3xl bg-white/5 border border-white/10" />
            <div className="h-48 rounded-3xl bg-white/5 border border-white/10" />
          </div>
          <div className="lg:col-span-4">
            <div className="h-96 rounded-3xl bg-white/5 border border-white/10" />
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // B. ERROR STATE (Retryable)
  // ============================================================
  if (error) {
    return (
      <div className="w-full max-w-lg mx-auto px-5 py-24 text-center">
        <div className="rounded-3xl border border-destructive/20 bg-destructive/5 p-8 sm:p-10 backdrop-blur-xl shadow-xl space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-destructive/10 border border-destructive/20 text-destructive mx-auto flex items-center justify-center">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-white">Unable to Load Workshop</h2>
          <p className="text-xs sm:text-sm text-white/65 leading-relaxed">
            {error} Please check your connection and try again.
          </p>
          <div className="pt-2">
            <Button
              onClick={loadWorkshop}
              className="rounded-xl bg-primary text-black hover:bg-primary/90 font-semibold gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Try Again</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // C. 404 NOT FOUND STATE (Drafts & Non-existent)
  // ============================================================
  if (!workshop) {
    return (
      <div className="w-full max-w-xl mx-auto px-5 py-24 text-center">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-12 backdrop-blur-xl shadow-2xl space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto flex items-center justify-center">
            <Search className="w-7 h-7" />
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            Workshop Not Found
          </h2>

          <p className="text-sm text-white/60 leading-relaxed max-w-md mx-auto">
            The workshop you are looking for is unavailable, unpublished, or may have
            been removed from our public catalog.
          </p>

          <div className="pt-3">
            <Button
              onClick={() => navigate("/workshops")}
              className="rounded-xl bg-primary text-black hover:bg-primary/90 font-semibold px-6 shadow-[0_0_20px_rgba(0,194,255,0.4)]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>Browse All Workshops</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check if Prerequisites or Requirements exist
  const hasPrerequisites =
    Array.isArray(workshop.prerequisites) && workshop.prerequisites.length > 0;
  const hasRequirements =
    Array.isArray(workshop.requirements) && workshop.requirements.length > 0;

  // ============================================================
  // D. MAIN WORKSHOP DETAIL VIEW
  // ============================================================
  return (
    <div className="w-full px-5 py-8 sm:py-10 xl:px-10 2xl:px-14 max-w-7xl mx-auto space-y-10 pb-28 md:pb-12">
      {/* 1. Detail Hero (Breadcrumbs, Title, Badges, Key Info Strip) */}
      <Reveal delay={0}>
        <WorkshopDetailHero workshop={workshop} />
      </Reveal>

      {/* 2. Responsive Content Grid (65% Content / 35% Sticky Sidebar) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 items-start">
        {/* ========================================================== */}
        {/* Left Column (Main Content — 65%)                           */}
        {/* ========================================================== */}
        <div className="lg:col-span-8 space-y-8 sm:space-y-10 order-2 lg:order-1">
          {/* Section: Overview */}
          <Reveal delay={60}>
            <section
              aria-labelledby="overview-heading"
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 backdrop-blur-xl shadow-[0_18px_50px_rgba(0,0,0,0.35)]"
            >
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-8 h-8 rounded-xl bg-primary/15 border border-primary/25 text-primary flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h2
                    id="overview-heading"
                    className="text-xl sm:text-2xl font-bold tracking-tight text-white"
                  >
                    Workshop Overview
                  </h2>
                  <p className="text-xs sm:text-sm text-white/60">
                    Real-world motivation, architecture focus, and hands-on depth.
                  </p>
                </div>
              </div>

              {/* Safe narrative parser */}
              <SafeOverviewRenderer content={workshop.description} />
            </section>
          </Reveal>

          {/* Section: Learning Outcomes */}
          <Reveal delay={100}>
            <WorkshopLearningOutcomes
              learningOutcomes={workshop.learning_outcomes}
            />
          </Reveal>

          {/* Section: Prerequisites & Requirements (hidden if both empty) */}
          {(hasPrerequisites || hasRequirements) && (
            <Reveal delay={110}>
              <div
                className={`grid grid-cols-1 ${
                  hasPrerequisites && hasRequirements ? "md:grid-cols-2" : ""
                } gap-6`}
              >
                {/* Prerequisites */}
                {hasPrerequisites && (
                  <section
                    aria-labelledby="prerequisites-heading"
                    className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-7 backdrop-blur-xl shadow-[0_18px_50px_rgba(0,0,0,0.35)]"
                  >
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-4 h-4" />
                      </div>
                      <h2
                        id="prerequisites-heading"
                        className="text-base sm:text-lg font-bold text-white tracking-tight"
                      >
                        Prerequisites
                      </h2>
                    </div>

                    <ul className="space-y-2.5">
                      {workshop.prerequisites.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2.5 text-xs sm:text-sm text-white/75"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* System Requirements */}
                {hasRequirements && (
                  <section
                    aria-labelledby="requirements-heading"
                    className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-7 backdrop-blur-xl shadow-[0_18px_50px_rgba(0,0,0,0.35)]"
                  >
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                        <Laptop className="w-4 h-4" />
                      </div>
                      <h2
                        id="requirements-heading"
                        className="text-base sm:text-lg font-bold text-white tracking-tight"
                      >
                        System Requirements
                      </h2>
                    </div>

                    <ul className="space-y-2.5">
                      {workshop.requirements.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2.5 text-xs sm:text-sm text-white/75"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </Reveal>
          )}

          {/* Section: Agenda / Timeline */}
          <Reveal delay={120}>
            <WorkshopAgenda agenda={workshop.agenda} />
          </Reveal>

          {/* Section: Session Highlights & Lab Moments (Strictly post-event feature) */}
          {workshop.is_highlights_eligible && Array.isArray(workshop.highlights) && workshop.highlights.length > 0 && (
            <Reveal delay={160}>
              <WorkshopGallery workshop={workshop} highlights={workshop.highlights} />
            </Reveal>
          )}
        </div>

        {/* ========================================================== */}
        {/* Right Column (Sticky Sidebar — 35%)                        */}
        {/* ========================================================== */}
        <aside
          aria-label="Workshop Information Sidebar"
          className="lg:col-span-4 order-1 lg:order-2 space-y-6 sm:space-y-8 lg:sticky lg:top-28 self-start h-fit"
        >
          <WorkshopStickyCTA workshop={workshop} />

          {/* Section: Instructor */}
          <Reveal delay={180}>
            <WorkshopInstructorCard
              instructor={workshop.instructor}
              instructors={workshop.instructors}
            />
          </Reveal>
        </aside>
      </div>

      {/* 3. Related Workshops Section (omitted if no other workshops exist) */}
      {relatedWorkshops.length > 0 && (
        <Reveal delay={100}>
          <div className="pt-10 border-t border-white/10 space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <span className="text-xs font-semibold text-primary uppercase tracking-wider block mb-1">
                  Explore More Sessions
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  Related Technical Workshops
                </h2>
              </div>

              <Link
                to="/workshops"
                className="text-xs font-semibold text-primary hover:text-white transition-colors inline-flex items-center gap-1.5"
              >
                <span>View Full Catalog</span>
                <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {relatedWorkshops.map((related) => (
                <WorkshopCard key={related.id} workshop={related} />
              ))}
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
}
